/**
 * ==========================================================================
 *  THE GLOBAL LEADERBOARD
 * ==========================================================================
 *  Deliberately separate from `ScoreRepository`. The personal best is local,
 *  instant and works on a train; the leaderboard is remote, slow and may not
 *  exist at all. Conflating them would mean a dropped connection could cost
 *  you a PB, and that a run cannot be raced offline.
 *
 *  The submission protocol is three steps, and the middle one is the point:
 *
 *    1. openTicket(category)  - before the run. The server issues a signed,
 *       single-use run id bound to the category and stamped with the time it
 *       was issued.
 *    2. play                  - the game records every input.
 *    3. submit(...)           - the replay goes up. The server re-simulates it
 *       and computes the time itself.
 *
 *  Note what is *not* in `submit`: a time. There is no field for one, because
 *  the server would not read it if there were.
 * ==========================================================================
 */

import type { IntoxicationLevel } from '../config/intoxication';
import type { Split } from '../world/Checkpoint';

export interface RunTicket {
  runId: string;
  /** Opaque signed token. Goes back up with the replay, unmodified. */
  token: string;
  category: string;
  /** Server clock, milliseconds. */
  issuedAt: number;
  expiresAt: number;
}

export interface LeaderboardEntry {
  runId: string;
  rank: number;
  name: string;
  /** The server's own number, from re-simulating the replay. */
  timeMs: number;
  category: string;
  intoxication: IntoxicationLevel;
  hannuHits: number;
  splits: Split[];
  recordedAt: number;
  /** True when a replay is stored; watching it requires its private replay id. */
  hasReplay: boolean;
}

export interface LeaderboardPage {
  category: string;
  entries: LeaderboardEntry[];
}

export type SubmissionOutcome =
  | { status: 'accepted'; replayId?: string; entry: LeaderboardEntry; rank: number; flagged: boolean }
  | { status: 'rejected'; reason: string }
  | { status: 'error'; reason: string };

export interface SubmissionRequest {
  token: string;
  name: string;
  /** The encoded replay. Everything else is derived from it, server-side. */
  replay: Uint8Array;
}

export interface LeaderboardClient {
  /** False when the game is running with no backend at all. */
  readonly isAvailable: boolean;
  openTicket(category: string): Promise<RunTicket | null>;
  submit(request: SubmissionRequest): Promise<SubmissionOutcome>;
  top(category: string, limit?: number): Promise<LeaderboardPage>;
  /** Download by private replay capability; a public run id grants no access. */
  replayUrl(replayId: string): string | null;
}

/**
 * What the game uses when there is no server: everything succeeds at doing
 * nothing. A LAN with no internet still gets the game, the timer and the local
 * personal best - it just does not get a shared board.
 */
export class OfflineLeaderboardClient implements LeaderboardClient {
  readonly isAvailable = false;

  async openTicket(): Promise<RunTicket | null> {
    return null;
  }

  async submit(): Promise<SubmissionOutcome> {
    return { status: 'error', reason: 'Tulostaulu ei ole käytettävissä.' };
  }

  async top(category: string): Promise<LeaderboardPage> {
    return { category, entries: [] };
  }

  replayUrl(): string | null {
    return null;
  }
}
